import React, { useMemo, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useHistory } from 'react-router-dom';
import {
  Box,
  Grid,
  Paper,
  List,
  ListItemButton,
  ListItemText,
  Avatar,
  Typography,
  Alert,
  CircularProgress,
  Skeleton,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  PhotoCamera,
  TextFields,
  ArrowDropDownCircle,
  CheckCircle,
  Close,
  WarningAmberRounded,
  EmojiObjects,
  InfoOutlined,
  ChatBubbleOutline,
} from '@mui/icons-material';
import { useSubmission } from 'services/Data/SubmissionServices';
import { useCriteriaTypes, useCriteriaTypeDetails } from 'services/Data/CriteriaServices';
import { translateLabel } from 'services/Utilities/labelUtils';
import { detailAppliesToSubmission, orderDetailsByHierarchy } from 'services/Utilities/criteriaDetailUtils';
import { CriteriaTypeDetailTypeEnumValue } from 'enums/CriteriaTypeDetailTypeEnum';
import { CertificationStatusEnumValue } from 'enums/CertificationStatusEnum';
import { SubmissionDetailStatusEnumValue } from 'enums/SubmissionDetailStatusEnum';
import CriteriaTypeSection from './CriteriaTypeSection';
import CriteriaNavigationBar from './CriteriaNavigationBar';
import { VisibilityRefreshProvider } from './VisibilityRefreshContext';

const TOP_NAV_OFFSET = 120;

const hasNonEmptyValue = value => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  return true;
};

const SidebarView = ({ isReadOnly = false, hasAvailableCriteriaOptions = true }) => {
  const { submissionId, criteriaTypeId, stepNumber } = useParams();
  const isSummaryStep = stepNumber === '4';
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const currentLanguage = i18n.language;
  const contentTopRef = useRef(null);
  const [isVisibilityRefreshInProgress, setIsVisibilityRefreshInProgress] = useState(false);

  const isZeroOnZero = useCallback((uploaded, total) => uploaded === 0 && total === 0, []);

  const { data: submission, isLoading: isLoadingSubmission, error: submissionError } = useSubmission(submissionId);
  const { data: allCriteriaTypes, isLoading: isLoadingTypes, error: criteriaTypesError } = useCriteriaTypes();
  const { data: allCriteriaTypeDetails, isLoading: isLoadingDetails, error: detailsError } = useCriteriaTypeDetails(
    submissionId,
  );

  const isLoading = isLoadingSubmission || isLoadingTypes || isLoadingDetails;
  const error = submissionError || criteriaTypesError || detailsError;

  const isRefreshingVisibility = isVisibilityRefreshInProgress;

  const heroUnitProductLineId = submission?.heroUnitProductLine_Id || submission?.heroUnitProductLine?.id || null;
  const attemptedOptionId = submission?.attemptedCriteriaOption_Id || null;
  const requiresHeroUnitSelection = submission?.criteria?.isHeroUnitVisible !== false;
  const dealerProductLineIds = useMemo(
    () => submission?.dealer?.dealerProductLines?.map(line => line.id).filter(Boolean) || [],
    [submission?.dealer?.dealerProductLines],
  );

  const submissionDetails = submission?.submissionDetails || [];

  const submissionDetailValueByDetailId = useMemo(() => {
    const map = new Map();
    submissionDetails.forEach(detail => {
      if (!detail?.criteriaTypeDetail_Id) return;
      map.set(detail.criteriaTypeDetail_Id, detail.value ?? '');
    });
    return map;
  }, [submissionDetails]);

  const shouldIncludeDetail = useCallback(
    detail =>
      detailAppliesToSubmission(detail, {
        attemptedCriteriaOptionId: attemptedOptionId,
        heroUnitProductLineId,
        dealerProductLineIds,
        submissionDetailValueByDetailId,
        requiresHeroUnitSelection,
        hasAvailableCriteriaOptions,
      }),
    [
      attemptedOptionId,
      dealerProductLineIds,
      heroUnitProductLineId,
      submissionDetailValueByDetailId,
      requiresHeroUnitSelection,
      hasAvailableCriteriaOptions,
    ],
  );

  const eligibleDetailsByType = useMemo(() => {
    if (!allCriteriaTypeDetails || (requiresHeroUnitSelection && !heroUnitProductLineId)) return new Map();

    const grouped = new Map();
    allCriteriaTypeDetails.forEach(detail => {
      if (!detail?.criteriaType_Id) return;
      const existing = grouped.get(detail.criteriaType_Id) ?? [];
      existing.push(detail);
      grouped.set(detail.criteriaType_Id, existing);
    });

    const filtered = new Map();
    grouped.forEach((details, criteriaTypeId) => {
      const eligible = details.filter(detail => shouldIncludeDetail(detail));
      const ordered = orderDetailsByHierarchy(eligible);
      if (ordered.length > 0) filtered.set(criteriaTypeId, ordered);
    });

    return filtered;
  }, [allCriteriaTypeDetails, heroUnitProductLineId, requiresHeroUnitSelection, shouldIncludeDetail]);

  const eligibleCriteriaTypes = useMemo(() => {
    if (!allCriteriaTypes || !submission?.criteria_Id) return [];

    return allCriteriaTypes
      .filter(ct => ct.criteria_Id === submission.criteria_Id)
      .filter(ct => {
        const optionIds = ct.criteriaTypeCriteriaOptions?.map(join => join?.criteriaOption_Id) || [];
        if (attemptedOptionId && !optionIds.includes(attemptedOptionId)) return false;

        const productLineIds = ct.criteriaTypeProductLines?.map(join => join?.productLine_Id) || [];
        if (requiresHeroUnitSelection) {
          if (!heroUnitProductLineId || !productLineIds.includes(heroUnitProductLineId)) return false;
        } else if (heroUnitProductLineId && !productLineIds.includes(heroUnitProductLineId)) return false;

        const dealerLines = ct.criteriaTypeDealerProductLines?.map(join => join?.productLine_Id) || [];
        if (dealerLines.length && !dealerLines.some(id => dealerProductLineIds.includes(id))) return false;

        return true;
      })
      .sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [
    allCriteriaTypes,
    submission?.criteria_Id,
    attemptedOptionId,
    heroUnitProductLineId,
    requiresHeroUnitSelection,
    dealerProductLineIds,
  ]);

  const currentCriteriaType = useMemo(
    () => eligibleCriteriaTypes.find(ct => ct.id === criteriaTypeId) || eligibleCriteriaTypes[0],
    [criteriaTypeId, eligibleCriteriaTypes],
  );

  const currentDetails = useMemo(() => {
    if (!currentCriteriaType) return [];
    return eligibleDetailsByType.get(currentCriteriaType.id) || [];
  }, [currentCriteriaType, eligibleDetailsByType]);

  const scrollContentIntoView = useCallback(() => {
    if (!contentTopRef.current) return;
    const rect = contentTopRef.current.getBoundingClientRect();
    const targetTop = Math.max(window.pageYOffset + rect.top - TOP_NAV_OFFSET, 0);
    window.scrollTo({ top: targetTop, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    if (isSummaryStep || !currentCriteriaType?.id) return;
    scrollContentIntoView();
  }, [currentCriteriaType?.id, isSummaryStep, scrollContentIntoView]);

  const isRequiredPhotoDetail = useCallback(
    detail => detail?.criteriaTypeDetailType === CriteriaTypeDetailTypeEnumValue.Photo && detail?.isRequired !== false,
    [],
  );

  const isRequiredInputDetail = useCallback(
    detail => detail?.criteriaTypeDetailType === CriteriaTypeDetailTypeEnumValue.Input && detail?.isRequired !== false,
    [],
  );

  const isRequiredSelectDetail = useCallback(
    detail => detail?.criteriaTypeDetailType === CriteriaTypeDetailTypeEnumValue.Dropdown && detail?.isRequired !== false,
    [],
  );

  const buildRequiredDetailIds = useCallback(
    predicate => {
      const ids = new Set();
      eligibleCriteriaTypes.forEach(criteriaType => {
        const eligibleDetails = eligibleDetailsByType.get(criteriaType.id) ?? [];
        eligibleDetails.forEach(detail => {
          if (predicate(detail)) ids.add(detail.id);
        });
      });
      return ids;
    },
    [eligibleCriteriaTypes, eligibleDetailsByType],
  );

  const buildCompletedDetailIds = useCallback(
    (requiredIds, completionPredicate) => {
      const completed = new Set();
      if (!submissionDetails.length || requiredIds.size === 0) return completed;

      submissionDetails.forEach(detail => {
        if (!detail?.criteriaTypeDetail_Id || !requiredIds.has(detail.criteriaTypeDetail_Id)) return;
        if (completionPredicate(detail)) completed.add(detail.criteriaTypeDetail_Id);
      });

      return completed;
    },
    [submissionDetails],
  );

  const allPhotoDetailIds = useMemo(() => buildRequiredDetailIds(isRequiredPhotoDetail), [
    buildRequiredDetailIds,
    isRequiredPhotoDetail,
  ]);
  const allInputDetailIds = useMemo(() => buildRequiredDetailIds(isRequiredInputDetail), [
    buildRequiredDetailIds,
    isRequiredInputDetail,
  ]);
  const allSelectDetailIds = useMemo(() => buildRequiredDetailIds(isRequiredSelectDetail), [
    buildRequiredDetailIds,
    isRequiredSelectDetail,
  ]);

  const completedPhotoDetailIds = useMemo(
    () => buildCompletedDetailIds(allPhotoDetailIds, detail => Array.isArray(detail.files) && detail.files.length > 0),
    [allPhotoDetailIds, buildCompletedDetailIds],
  );

  const completedInputDetailIds = useMemo(
    () => buildCompletedDetailIds(allInputDetailIds, detail => hasNonEmptyValue(detail.value)),
    [allInputDetailIds, buildCompletedDetailIds],
  );

  const completedSelectDetailIds = useMemo(
    () => buildCompletedDetailIds(allSelectDetailIds, detail => hasNonEmptyValue(detail.value)),
    [allSelectDetailIds, buildCompletedDetailIds],
  );

  const hasUploadedAllPhotos = allPhotoDetailIds.size === 0 || completedPhotoDetailIds.size === allPhotoDetailIds.size;
  const hasCompletedAllRequiredInputs =
    allInputDetailIds.size === 0 || completedInputDetailIds.size === allInputDetailIds.size;
  const hasCompletedAllRequiredSelects =
    allSelectDetailIds.size === 0 || completedSelectDetailIds.size === allSelectDetailIds.size;
  const hasCompletedAllRequirements =
    hasUploadedAllPhotos && hasCompletedAllRequiredInputs && hasCompletedAllRequiredSelects;
  const hasAnyRequirements = allPhotoDetailIds.size > 0 || allInputDetailIds.size > 0 || allSelectDetailIds.size > 0;

  const completedDetailIds = useMemo(() => {
    const ids = new Set();
    completedPhotoDetailIds.forEach(id => ids.add(id));
    completedInputDetailIds.forEach(id => ids.add(id));
    completedSelectDetailIds.forEach(id => ids.add(id));
    return ids;
  }, [completedPhotoDetailIds, completedInputDetailIds, completedSelectDetailIds]);

  const uploadedPhotoCount = useMemo(() => {
    let count = 0;
    allPhotoDetailIds.forEach(detailId => {
      const match = submissionDetails.find(sd => sd.criteriaTypeDetail_Id === detailId);
      if (match?.files?.length) count += match.files.length;
    });
    return count;
  }, [allPhotoDetailIds, submissionDetails]);

  const activeCriteriaIndex = useMemo(() => {
    if (isSummaryStep || !eligibleCriteriaTypes) return -1;
    if (!criteriaTypeId) return 0;
    const index = eligibleCriteriaTypes.findIndex(ct => ct.id === criteriaTypeId);
    return index >= 0 ? index : 0;
  }, [criteriaTypeId, eligibleCriteriaTypes, isSummaryStep]);

  useEffect(() => {
    if (isSummaryStep) return;
    if (!submissionId || isLoading || !eligibleCriteriaTypes.length) return;
    if (!criteriaTypeId || !eligibleCriteriaTypes.some(ct => ct.id === criteriaTypeId)) {
      history.replace(
        `/dealer/certification/submissions/${submissionId}/step/${stepNumber || 3}/${eligibleCriteriaTypes[0].id}`,
      );
    }
  }, [criteriaTypeId, eligibleCriteriaTypes, history, isLoading, isSummaryStep, stepNumber, submissionId]);

  if (isLoading) {
    return (
      <Box sx={{ py: 6, textAlign: 'center' }}>
        <CircularProgress sx={theme => ({ color: theme.palette.primary.main })} />
        <Typography sx={{ mt: 2, color: 'text.secondary' }}>{t('common.loading')}</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {t('submission.loadingFailed')} {error.message}
      </Alert>
    );
  }

  if (!submission) {
    return (
      <Alert severity="warning" sx={{ mb: 2 }}>
        {t('submission.notFound')}
      </Alert>
    );
  }

  const totalRequiredPhotos = allPhotoDetailIds.size;

  const statusIsLocked =
    submission.status === CertificationStatusEnumValue.AwaitingReview ||
    submission.status === CertificationStatusEnumValue.ReviewInProgress;

  const effectiveActiveIndex = activeCriteriaIndex >= 0 ? activeCriteriaIndex : 0;
  const currentCriteriaLabel = currentCriteriaType ? translateLabel(currentCriteriaType.name, currentLanguage) : null;
  const isCertified = submission?.status === CertificationStatusEnumValue.Passed;
  const pastQuarter = submission?.quarter?.endDate ? new Date(submission.quarter.endDate) <= new Date() : false;
  const isAdvancingToSummary = eligibleCriteriaTypes.length > 0 && effectiveActiveIndex === eligibleCriteriaTypes.length - 1;

  const pendingSectionLabels = eligibleCriteriaTypes?.length
    ? eligibleCriteriaTypes.reduce((sections, criteriaType) => {
        const details = eligibleDetailsByType.get(criteriaType.id) || [];
        const needsAttention = details.some(detail => {
          const isTracked = isRequiredPhotoDetail(detail) || isRequiredInputDetail(detail) || isRequiredSelectDetail(detail);
          if (!isTracked) return false;
          return !completedDetailIds.has(detail.id);
        });

        if (needsAttention) {
          sections.push(translateLabel(criteriaType.name, currentLanguage));
        }

        return sections;
      }, [])
    : [];

  const handleStepClick = index => {
    if (!eligibleCriteriaTypes?.length) return;
    const targetCriteriaType = eligibleCriteriaTypes[index];
    if (targetCriteriaType) {
      history.push(`/dealer/certification/submissions/${submissionId}/step/${stepNumber || 3}/${targetCriteriaType.id}`);
    }
  };

  const handlePrevious = () => {
    if (!eligibleCriteriaTypes.length) {
      history.push(`/dealer/certification/submissions/${submissionId}/step/2`);
      return;
    }

    if (effectiveActiveIndex <= 0) {
      history.push(`/dealer/certification/submissions/${submissionId}/step/2`);
      return;
    }

    handleStepClick(effectiveActiveIndex - 1);
  };

  const handleNext = () => {
    if (!eligibleCriteriaTypes.length) return;

    if (isAdvancingToSummary) {
      if (!hasCompletedAllRequirements && !isCertified && !pastQuarter) return;
      history.push(`/dealer/certification/submissions/${submissionId}/step/4`);
      return;
    }

    handleStepClick(effectiveActiveIndex + 1);
  };

  return (
    <Box>
      <Grid container spacing={3} sx={{ mt: 1 }}>
        <Grid item xs={12} md={4} lg={3}>
          <Paper
            data-tour="submissionSteps"
            elevation={2}
            sx={{
              p: 0,
              position: 'sticky',
              top: 132,
              zIndex: 1,
              bgcolor: theme => theme.palette.background.paper,
            }}>
            <List dense>
              {eligibleCriteriaTypes.map((ct, index) => {
                const details = eligibleDetailsByType.get(ct.id) || [];
                const requiredPhotos = details.filter(isRequiredPhotoDetail);
                const requiredInputs = details.filter(isRequiredInputDetail);
                const requiredSelects = details.filter(isRequiredSelectDetail);
                const photoDetailIds = requiredPhotos.map(detail => detail.id);

                const statusCount = statusValue =>
                  submission.submissionDetails?.filter(
                    sd => sd.status === statusValue && photoDetailIds.includes(sd.criteriaTypeDetail_Id),
                  ).length || 0;

                const uploadedPhotos = requiredPhotos.reduce((count, detail) => {
                  const sd = submission.submissionDetails?.find(s => s.criteriaTypeDetail_Id === detail.id);
                  return count + (sd?.files?.length || 0);
                }, 0);

                const completedInputs = requiredInputs.filter(detail => {
                  const sd = submission.submissionDetails?.find(s => s.criteriaTypeDetail_Id === detail.id);
                  return hasNonEmptyValue(sd?.value);
                }).length;

                const completedSelects = requiredSelects.filter(detail => {
                  const sd = submission.submissionDetails?.find(s => s.criteriaTypeDetail_Id === detail.id);
                  return hasNonEmptyValue(sd?.value);
                }).length;

                const isActive = currentCriteriaType?.id === ct.id;

                return (
                  <ListItemButton
                    key={ct.id}
                    selected={isActive}
                    onClick={() =>
                      history.push(`/dealer/certification/submissions/${submissionId}/step/${stepNumber || 3}/${ct.id}`)
                    }
                    sx={{
                      p: 2,
                      borderLeft: theme => (isActive ? `3px solid ${theme.palette.primary.main}` : '3px solid transparent'),
                      borderRadius: 0,
                    }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2 }}>
                      {/* Step Number */}
                      <Box
                        sx={theme => ({
                          width: 32,
                          height: 32,
                          mr: 1,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor:
                            activeCriteriaIndex === index
                              ? '#FFD200'
                              : theme.palette.mode === 'dark'
                              ? 'rgba(193, 193, 193, 0.1)'
                              : 'rgba(0, 0, 0, 0.08)',
                          color: activeCriteriaIndex === index ? '#000' : theme.palette.text.secondary,
                          fontWeight: 700,
                          flexShrink: 0,
                          border: activeCriteriaIndex === index ? 'none' : `1px solid ${theme.palette.borderColor.main}`,
                        })}>
                        {index + 1}
                      </Box>

                      <ListItemText
                        primary={translateLabel(ct.name, currentLanguage)}
                        primaryTypographyProps={{
                          sx: theme => ({
                            color:
                              activeCriteriaIndex === index
                                ? theme.palette.mode === 'dark'
                                  ? '#FFD200'
                                  : '#000000'
                                : theme.palette.mode === 'dark'
                                ? theme.palette.text.primary
                                : '#000000',
                            fontSize: '1.25rem',

                            fontWeight: activeCriteriaIndex === index ? 700 : 500,
                            noWrap: true,
                          }),
                          variant: 'body2',
                        }}
                        secondaryTypographyProps={{
                          sx: theme => ({
                            color: theme.palette.text.disabled,
                            mt: 0.5,
                          }),
                        }}
                      />
                    </Box>
                    <Box
                      sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end', ml: 1 }}
                      data-tour="submissionStepsInfo">
                      {!isZeroOnZero(uploadedPhotos, requiredPhotos.length) && submission?.submitToReviewCount === 0 ? (
                        <Tooltip title={t('submission.requiredPhotosUploaded')} arrow>
                          <Chip
                            icon={<PhotoCamera sx={{ fontSize: 14 }} />}
                            label={`${uploadedPhotos}/${requiredPhotos.length}`}
                            size="small"
                            sx={theme => ({
                              fontWeight: 600,
                              ...(uploadedPhotos >= requiredPhotos.length
                                ? {
                                    bgcolor: theme.palette.info.light,
                                    border: `1px solid ${theme.palette.info.main}`,
                                    color: theme.palette.text.white,
                                    '& .MuiChip-icon': { color: theme.palette.text.white },
                                    '& .MuiChip-label': { color: theme.palette.text.white, fontWeight: 700 },
                                  }
                                : {}),
                            })}
                          />
                        </Tooltip>
                      ) : null}
                      {requiredInputs.length && submission?.submitToReviewCount === 0 ? (
                        <Tooltip title={t('submission.requiredInputsCompleted')} arrow>
                          <Chip
                            icon={<TextFields sx={{ fontSize: 14 }} />}
                            label={`${completedInputs}/${requiredInputs.length}`}
                            size="small"
                            sx={theme => ({
                              fontWeight: 600,
                              ...(completedInputs >= requiredInputs.length
                                ? {
                                    bgcolor: theme.palette.info.light,
                                    border: `1px solid ${theme.palette.info.main}`,
                                    color: theme.palette.text.white,
                                    '& .MuiChip-icon': { color: theme.palette.text.white },
                                    '& .MuiChip-label': { color: theme.palette.text.white, fontWeight: 700 },
                                  }
                                : {}),
                            })}
                          />
                        </Tooltip>
                      ) : null}
                      {requiredSelects.length && submission?.submitToReviewCount === 0 ? (
                        <Tooltip title={t('submission.requiredSelectsCompleted')} arrow>
                          <Chip
                            icon={<ArrowDropDownCircle sx={{ fontSize: 14 }} />}
                            label={`${completedSelects}/${requiredSelects.length}`}
                            size="small"
                            sx={theme => ({
                              fontWeight: 600,
                              ...(completedSelects >= requiredSelects.length
                                ? {
                                    bgcolor: theme.palette.info.light,
                                    border: `1px solid ${theme.palette.info.main}`,
                                    color: theme.palette.text.white,
                                    '& .MuiChip-icon': { color: theme.palette.text.white },
                                    '& .MuiChip-label': { color: theme.palette.text.white, fontWeight: 700 },
                                  }
                                : {}),
                            })}
                          />
                        </Tooltip>
                      ) : null}
                      {statusCount(SubmissionDetailStatusEnumValue.Accepted) ? (
                        <Tooltip title={t('submission.status.accepted')} arrow>
                          <Chip
                            icon={<CheckCircle sx={{ fontSize: 14 }} />}
                            label={statusCount(SubmissionDetailStatusEnumValue.Accepted)}
                            size="small"
                            sx={{ bgcolor: 'rgba(102,187,106,0.15)', color: '#66BB6A', fontWeight: 600 }}
                          />
                        </Tooltip>
                      ) : null}
                      {statusCount(SubmissionDetailStatusEnumValue.NeedsWork) ? (
                        <Tooltip title={t('submission.status.needsWork')} arrow>
                          <Chip
                            icon={<Close sx={{ fontSize: 14 }} />}
                            label={statusCount(SubmissionDetailStatusEnumValue.NeedsWork)}
                            size="small"
                            sx={{ bgcolor: 'rgba(239,83,80,0.15)', color: '#EF5350', fontWeight: 600 }}
                          />
                        </Tooltip>
                      ) : null}
                      {statusCount(SubmissionDetailStatusEnumValue.ImprovementRequired) ? (
                        <Tooltip title={t('submission.status.improvementRequired')} arrow>
                          <Chip
                            icon={<WarningAmberRounded sx={{ fontSize: 14 }} />}
                            label={statusCount(SubmissionDetailStatusEnumValue.ImprovementRequired)}
                            size="small"
                            sx={{ bgcolor: 'rgba(255,152,0,0.2)', color: '#FF9800', fontWeight: 600 }}
                          />
                        </Tooltip>
                      ) : null}
                      {statusCount(SubmissionDetailStatusEnumValue.BrpBestPractice) ? (
                        <Tooltip title={t('submission.status.brpBestPractice')} arrow>
                          <Chip
                            icon={<EmojiObjects sx={{ fontSize: 14 }} />}
                            label={statusCount(SubmissionDetailStatusEnumValue.BrpBestPractice)}
                            size="small"
                            sx={{ bgcolor: 'rgba(255,234,58,0.22)', color: 'text.primary', fontWeight: 600 }}
                          />
                        </Tooltip>
                      ) : null}
                    </Box>
                  </ListItemButton>
                );
              })}
            </List>
          </Paper>
        </Grid>

        <Grid item xs={12} md={8} lg={9} ref={contentTopRef}>
          {statusIsLocked ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              {t('submission.awaitingReviewLockedDescription')}
            </Alert>
          ) : null}
          <Box
            data-tour="submissionRequiredOptional"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexWrap: 'wrap',
              mb: 2,
            }}>
            <Chip
              label={t('submission.requiredPhoto')}
              size="small"
              sx={{
                fontWeight: 600,
                color: theme => theme.palette.info.dark,
                border: theme => `1px solid ${theme.palette.info.main}`,
                bgcolor: theme => (theme.palette.mode === 'dark' ? 'rgba(2, 136, 209, 0.2)' : 'rgba(2, 136, 209, 0.08)'),
                '& .MuiChip-icon': {
                  color: theme => theme.palette.info.dark,
                },
              }}
            />
            <Chip
              label={t('submission.optionalPhoto')}
              size="small"
              sx={{
                fontWeight: 600,
                border: theme => `1px dashed ${theme.palette.divider}`,
                bgcolor: theme => theme.palette.action.hover,
                color: theme => theme.palette.text.disabled,
                '& .MuiChip-icon': {
                  color: theme => theme.palette.text.disabled,
                },
              }}
            />
            <Typography variant="caption" sx={{ color: theme => theme.palette.text.primary }}>
              {t('submission.requiredPhotoLegend')}
            </Typography>
          </Box>
          <VisibilityRefreshProvider value={{ setIsVisibilityRefreshInProgress }}>
            {isRefreshingVisibility ? (
              <Box sx={{ mt: 1 }}>
                <Skeleton variant="text" width="45%" height={34} />
                <Skeleton variant="rounded" height={72} sx={{ mt: 2 }} />
                <Skeleton variant="rounded" height={72} sx={{ mt: 2 }} />
                <Skeleton variant="rounded" height={180} sx={{ mt: 2 }} />
              </Box>
            ) : (
              <CriteriaTypeSection
                criteriaType={currentCriteriaType}
                details={currentDetails}
                submissionDetails={submission?.submissionDetails}
                isReadOnly={isReadOnly || statusIsLocked}
              />
            )}
          </VisibilityRefreshProvider>
        </Grid>
      </Grid>

      {eligibleCriteriaTypes.length ? (
        <Box sx={{ mt: 3 }}>
          <CriteriaNavigationBar
            currentIndex={effectiveActiveIndex}
            totalCriteria={eligibleCriteriaTypes.length}
            currentLabel={currentCriteriaLabel}
            onPrevious={handlePrevious}
            onNext={handleNext}
            isPreviousDisabled={isRefreshingVisibility}
            isNextDisabled={
              isRefreshingVisibility ||
              (isAdvancingToSummary && !hasCompletedAllRequirements && !isCertified && !pastQuarter)
            }
            advancingToSummary={isAdvancingToSummary}
            showSummaryGuard={
              isAdvancingToSummary && !hasCompletedAllRequirements && hasAnyRequirements && !isCertified && !pastQuarter
            }
            pendingSectionLabels={pendingSectionLabels}
          />
        </Box>
      ) : null}
    </Box>
  );
};

export default SidebarView;
